import { Link } from '@tanstack/react-router';
import { CaretRight, Lock, Plus } from '@phosphor-icons/react';
import type { TaskSummary } from '@mybusinessmate/schema';
import { clientStatus, dueLabel, staffStatus, TASK_TYPE_META } from '../../lib/task-display';
import { cx } from '../../lib/cx';
import { initialsOf } from '../../lib/initials';
import { Icon } from '../Icon';
import { Avatar } from '../ui/avatar';
import { ProgressBar } from '../ui/progress-bar';
import { Skeleton } from '../ui/skeleton';
import { StatusTag } from '../ui/status-tag';

const subtitle = (task: TaskSummary, staff = false) => {
  const due = dueLabel(task.deadline);
  return (
    <span className="flex items-center gap-(--mbm-space-1) truncate">
      <span>{TASK_TYPE_META[task.taskType].short}</span>
      <span aria-hidden>·</span>
      <span className={cx(due?.urgent && 'text-feedback-warning')}>{due ? due.text : 'no date yet'}</span>
      {staff && task.milestoneTotal > 0 ? (
        <>
          <span aria-hidden>·</span>
          <span>
            milestone {Math.min(task.milestoneDone + 1, task.milestoneTotal)} of {task.milestoneTotal}
          </span>
        </>
      ) : null}
    </span>
  );
};

/** Columns of a task row (Figma A05.2): type icon, title, status, progress, assignee, chevron. Below `md`: icon, title, chevron. */
export const STAFF_TASK_GRID =
  'grid items-center gap-(--mbm-space-4) pl-(--mbm-space-6) pr-(--mbm-space-4) grid-cols-[auto_minmax(0,1fr)_auto] md:grid-cols-[auto_minmax(0,1fr)_auto_9.5rem_auto_auto]';

/**
 * A task on the staff project page (Figma A05.2): type icon, title (with "Hidden from client"), where it stands, the
 * specialist's manual progress, and who is on it.
 * TODO(api): the design's second line also carries version and approval notes ("v2 sent 16 Oct · needs client
 * approval"); those come with the review step's submissions.
 */
export function StaffTaskRow({ task }: { task: TaskSummary }) {
  const status = staffStatus(task.status);
  return (
    <Link
      to="/subdomain/admin/tasks/$taskId"
      params={{ taskId: task.id }}
      className={`${STAFF_TASK_GRID} py-(--mbm-space-4) transition-colors duration-(--mbm-duration-fast) hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)`}
    >
      <span className="text-text-primary">
        <Icon icon={TASK_TYPE_META[task.taskType].icon} size="md" />
      </span>
      <span className="flex min-w-0 flex-col gap-(--mbm-space-0-5)">
        <span className="flex items-center gap-(--mbm-space-2)">
          <span className="truncate text-label-large text-text-primary">{task.title}</span>
          {!task.clientVisible ? (
            <span className="inline-flex shrink-0 items-center gap-(--mbm-space-1) rounded-(--mbm-radius-xs) border border-border-default px-(--mbm-space-1-5) text-label-small text-text-secondary">
              <Icon icon={Lock} size="xs" />
              Hidden from client
            </span>
          ) : null}
        </span>
        <span className="text-body-xsmall text-text-tertiary">{subtitle(task, true)}</span>
      </span>
      <StatusTag
        size="md"
        label={status.label}
        tone={status.tone}
        variant={status.tone === 'grey' ? 'outline' : 'soft'}
        className="hidden md:inline-flex"
      />
      <span className="hidden items-center gap-(--mbm-space-2) md:flex">
        <ProgressBar size="slim" value={task.progress} tone="dark" label={`${task.title} progress`} />
        <span className="w-(--mbm-space-8) text-right text-label-medium text-text-primary">{task.progress}%</span>
      </span>
      <span className="hidden w-(--mbm-size-avatar-xs) md:block">
        {task.assignee ? (
          <Avatar tone="neutral" size="xxs" initials={initialsOf(task.assignee)} label={task.assignee} />
        ) : (
          <span
            aria-label="Nobody on it yet"
            className="flex size-(--mbm-size-avatar-xxs) items-center justify-center rounded-full bg-surface-field text-text-tertiary"
          >
            <Icon icon={Plus} size="xs" />
          </span>
        )}
      </span>
      <span className="text-text-muted">
        <Icon icon={CaretRight} size="md" />
      </span>
    </Link>
  );
}

/** The 4px bar beside a client's task: pink while it waits on them, amber when changes were asked, the type's colour while in progress. */
const clientBar = (task: TaskSummary) => {
  const group = clientStatus(task.status).group;
  if (group === 'Waiting for you') return 'bg-accent-waiting';
  if (group === 'Changes requested') return 'bg-accent-changes';
  if (group === 'In progress') return task.taskType === 'reel' ? 'bg-accent-reel' : 'bg-accent-post';
  return 'bg-accent-queued';
};

/**
 * A task on the client's project page (Figma C03.2): a bar in the colour of where it stands, the title and the
 * client's own wording for its status. The row that waits on them sits on a pale blue ground.
 * TODO(api): the design's "version 2" and "new version expected 29 Sep" need the review step's versions.
 */
export function ClientTaskRow({ task }: { task: TaskSummary }) {
  const status = clientStatus(task.status);
  const className = cx(
    'flex items-center gap-(--mbm-space-3-5) rounded-(--mbm-radius-md) p-(--mbm-space-3)',
    status.group === 'Waiting for you' && 'bg-feedback-info-bg',
  );
  const body = (
    <>
      <span aria-hidden className={cx('h-(--mbm-space-5) w-(--mbm-space-1) shrink-0 rounded-full', clientBar(task))} />
      <span className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5)">
        <span className="truncate text-label-large text-text-primary">{task.title}</span>
        <span className="text-body-xsmall text-text-tertiary">{subtitle(task)}</span>
      </span>
      <StatusTag
        size="md"
        label={status.label}
        tone={status.tone}
        variant={status.tone === 'grey' ? 'outline' : 'soft'}
      />
      <span className="text-text-primary">
        <Icon icon={CaretRight} size="sm" />
      </span>
    </>
  );
  return (
    <Link
      to="/subdomain/app/tasks/$taskId"
      params={{ taskId: task.id }}
      className={cx(
        className,
        'hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
      )}
    >
      {body}
    </Link>
  );
}

export function TaskRowSkeleton() {
  return (
    <div className="flex items-center gap-(--mbm-space-4) px-(--mbm-space-6) py-(--mbm-space-4)">
      <Skeleton className="size-(--mbm-space-5)" />
      <div className="flex flex-1 flex-col gap-(--mbm-space-2)">
        <Skeleton className="h-(--mbm-space-5) w-1/2" />
        <Skeleton className="h-(--mbm-space-4) w-1/3" />
      </div>
    </div>
  );
}
